/**
 * The layout stage — story S2 of `q-dungeon-layout-generator-build` (AD-404;
 * docs/design/dungeon-layout-generation.md §4, amendments §12b). It turns the
 * topology graph (rooms on grid cells, connections between grid neighbours)
 * into sized chambers, straight corridors and one secret alcove, built so that
 * `validateDungeonLayout` (S1) passes by construction; the generator still runs
 * the validator and a failure feeds the ordinary retry ladder.
 *
 * Pure and deterministic: it draws only from the `random` it is handed, walks
 * rooms and connections in plan order, and rounds every number to the
 * millimetre. Client-local (O-025): no wire, no combat maths, no reward values.
 */
import { analyzeDungeonLayout } from './analyze.js';
import { DUNGEON_LAYOUT_PRECISION, DUNGEON_PLAYER_BODY, convexOverlap, dodgeTravelM, polygonArea, resolveEnemyFootprint, segmentFootprint, } from './layout.js';
/**
 * The interior numbers that stay global under the layout (§7 `interiorScale.ts`).
 * Derived exactly as `frontend/src/game/dungeon/interiorScale.ts` derives them
 * from the 3.30 m sentinel proxy: doorway 3.30 + 2 × 0.60 side clearance; the
 * secret entrance is the doorway less two 0.45 m jambs, its panel leaves a
 * 2 × 0.60 m gap; the alcove is one sentinel deep. S3 makes the client read
 * these instead of its own copy. `[tune]` values are marked.
 */
export const DUNGEON_LAYOUT_GLOBALS = Object.freeze({
    doorwayClearWidth: 4.5,
    doorwayJambWidth: 0.45,
    /** `[tune]` — the smallest chamber a player is ever put in (§4.1). */
    chamberMinInscribedRadius: 2.5,
    /** `[tune]` — standing room around each body in the occupancy sum (§4.1). */
    standingAllowance: Object.freeze({ enemy: 0.5, player: 0.6 }),
    secret: Object.freeze({ entranceClearWidth: 3.6, panelWidth: 2.4, alcoveDepth: 3.3 }),
    /** `[tune]` — ceiling of a chamber with no encounter: a player plus clearance. */
    minWallHeight: 3,
    /** Normal-width stub either side of a choke, so every doorway stays full width. */
    chokeDoorStub: 1,
    /** Clearance kept between neighbouring cell bands beyond the secret alcove's depth. */
    bandMargin: 0.5,
});
/** Intensity of each pacing beat (§4.4); `exhale` is also what rest chambers are pinned to. */
export const DUNGEON_BEAT_INTENSITY = Object.freeze({
    calm: 0.25,
    build: 0.5,
    peak: 1,
    exhale: 0,
});
const TAN_PI_8 = Math.tan(Math.PI / 8);
const COS_PI_8 = Math.cos(Math.PI / 8);
function mm(value) {
    return Math.round(value * DUNGEON_LAYOUT_PRECISION) / DUNGEON_LAYOUT_PRECISION;
}
function mmUp(value) {
    return Math.ceil(value * DUNGEON_LAYOUT_PRECISION - 1e-9) / DUNGEON_LAYOUT_PRECISION;
}
/**
 * The rules the validator reads, assembled from the catalogue. Throws when a
 * field S2 consumes is missing — `validateDungeonCatalog` refuses such a
 * catalogue first, so this is a programming error, not a data path.
 */
export function layoutRulesFor(catalog, profile, theme) {
    if (!profile.clearance)
        throw new Error(`profile '${profile.id}' has no clearance block`);
    if (!theme.layout)
        throw new Error(`theme '${theme.id}' has no layout block`);
    return {
        clearance: profile.clearance,
        compactnessFloor: theme.layout.compactnessFloor,
        doorwayClearWidth: DUNGEON_LAYOUT_GLOBALS.doorwayClearWidth,
        chamberMinInscribedRadius: DUNGEON_LAYOUT_GLOBALS.chamberMinInscribedRadius,
        standingAllowance: { ...DUNGEON_LAYOUT_GLOBALS.standingAllowance },
        secret: {
            entranceClearWidth: DUNGEON_LAYOUT_GLOBALS.secret.entranceClearWidth,
            panelWidth: DUNGEON_LAYOUT_GLOBALS.secret.panelWidth,
        },
        enemies: catalog.enemies,
        footprintDefaults: catalog.footprintDefaults,
    };
}
// ---------------------------------------------------------------- pacing (§4.4)
/**
 * Lays the profile's beats over the critical path: `boss`/`miniboss` pinned to
 * peak, `rest` and the exit to exhale, and each run of free chambers between
 * pins stretched over the profile's rising beats in order. Non-critical
 * chambers are 0.
 */
export function assignIntensity(rooms, criticalPathRoomIds, beats) {
    const out = new Map(rooms.map((room) => [room.id, 0]));
    const roleById = new Map(rooms.map((room) => [room.id, room.role]));
    const rising = beats.filter((beat) => beat !== 'exhale');
    const ramp = rising.length > 0 ? rising : ['calm'];
    let run = [];
    const flush = () => {
        run.forEach((id, index) => {
            const beat = ramp[Math.min(ramp.length - 1, Math.floor((index * ramp.length) / run.length))];
            out.set(id, DUNGEON_BEAT_INTENSITY[beat]);
        });
        run = [];
    };
    for (const id of criticalPathRoomIds) {
        const role = roleById.get(id);
        if (role === 'boss' || role === 'miniboss') {
            flush();
            out.set(id, DUNGEON_BEAT_INTENSITY.peak);
        }
        else if (role === 'rest' || role === 'exit') {
            flush();
            out.set(id, DUNGEON_BEAT_INTENSITY.exhale);
        }
        else {
            run.push(id);
        }
    }
    flush();
    return out;
}
const SIDES = ['+x', '-x', '+z', '-z'];
const SIDE_VECTOR = {
    '+x': { x: 1, z: 0 },
    '-x': { x: -1, z: 0 },
    '+z': { x: 0, z: 1 },
    '-z': { x: 0, z: -1 },
};
/** Exact quarter-turn rotation (cos, sin) taking local +z onto the side. */
const SIDE_ROTATION = {
    '+z': [1, 0],
    '-x': [0, 1],
    '-z': [-1, 0],
    '+x': [0, -1],
};
function sideOf(dx, dz) {
    if (dx === 1)
        return '+x';
    if (dx === -1)
        return '-x';
    return dz === 1 ? '+z' : '-z';
}
function rectPoints(minX, minZ, maxX, maxZ) {
    return [
        { x: minX, z: minZ },
        { x: maxX, z: minZ },
        { x: maxX, z: maxZ },
        { x: minX, z: maxZ },
    ];
}
function rotate(point, [c, s]) {
    return { x: point.x * c - point.z * s, z: point.x * s + point.z * c };
}
/** Drops repeated and collinear vertices so every outline edge is a real wall. */
function tidy(points) {
    let out = points.filter((point, index) => {
        const next = points[(index + 1) % points.length];
        return Math.abs(point.x - next.x) > 1e-9 || Math.abs(point.z - next.z) > 1e-9;
    });
    let changed = true;
    while (changed && out.length > 3) {
        changed = false;
        for (let i = 0; i < out.length; i += 1) {
            const prev = out[(i + out.length - 1) % out.length];
            const point = out[i];
            const next = out[(i + 1) % out.length];
            const cross = (point.x - prev.x) * (next.z - point.z) - (point.z - prev.z) * (next.x - point.x);
            if (Math.abs(cross) < 1e-9) {
                out = out.filter((_, index) => index !== i);
                changed = true;
                break;
            }
        }
    }
    return out;
}
/** The §4.1 floors for one chamber: required inscribed radius, floor area and wall height. */
function requirements(input) {
    const { room, rules, profile, theme } = input;
    const clearance = profile.clearance;
    const roleLayout = theme.layout.roles[room.role];
    const player = rules.player ?? DUNGEON_PLAYER_BODY;
    const minRadius = rules.chamberMinInscribedRadius;
    if (!room.encounter) {
        const radius = minRadius * roleLayout.volumeFactor;
        return {
            radius,
            area: (2 * radius) ** 2,
            wallHeight: DUNGEON_LAYOUT_GLOBALS.minWallHeight,
        };
    }
    const members = room.encounter.members.map((member) => {
        const footprint = resolveEnemyFootprint(member.enemyId, member.roles, rules);
        if (!footprint)
            throw new Error(`enemy '${member.enemyId}' has no footprint and no role default`);
        return { count: member.count, footprint };
    });
    const reach = Math.max(0, ...members.map((member) => member.footprint.telegraphReach));
    const radius = reach > 0 ? reach + dodgeTravelM(player) + player.collisionRadiusM : minRadius;
    const occupied = members.reduce((sum, member) => sum +
        member.count * Math.PI * (member.footprint.radius + rules.standingAllowance.enemy) ** 2, 0) +
        input.partySize * Math.PI * (player.collisionRadiusM + rules.standingAllowance.player) ** 2;
    const k = Math.max(1, clearance.kFree.max - input.intensity * (clearance.kFree.max - clearance.kFree.min));
    const tallest = Math.max(0, ...members.map((member) => member.footprint.height));
    return {
        radius,
        area: k * occupied * roleLayout.volumeFactor,
        wallHeight: Math.max(DUNGEON_LAYOUT_GLOBALS.minWallHeight, tallest + clearance.headroom),
    };
}
/** Half a doorway frame: a wall side narrower than this cannot carry a doorway. */
function doorHalfSpan(rules) {
    return rules.doorwayClearWidth / 2 + DUNGEON_LAYOUT_GLOBALS.doorwayJambWidth;
}
/** Sizes one chamber (§4.1/§4.2): shape from the role's vocabulary, dimensions from the floors. */
export function sizeChamber(input) {
    const { room, rules, random, doorSides } = input;
    const roleLayout = input.theme.layout.roles[room.role];
    const need = requirements(input);
    const halfDoor = doorHalfSpan(rules);
    const wallHeight = mmUp(need.wallHeight);
    const octagonRadius = Math.max(need.radius, Math.sqrt(need.area / (8 * TAN_PI_8)));
    const freeSides = SIDES.filter((side) => !doorSides.has(side));
    const shapes = roleLayout.shapes.filter((shape) => {
        // an octagon's flat side must carry a full doorway frame
        if (shape === 'octagon')
            return octagonRadius * TAN_PI_8 >= halfDoor;
        // an L/T wing needs a doorway-free side to grow from
        if (shape === 'l' || shape === 't')
            return freeSides.length > 0;
        return true;
    });
    const shape = shapes.length > 0 ? random.pick(shapes) : 'rect';
    if (shape === 'octagon') {
        // 3 mm over the floor: the vertex rounding may cost the inscribed disc < 1 mm.
        const radius = mmUp(octagonRadius + 0.003);
        const circum = radius / COS_PI_8;
        const outline = Array.from({ length: 8 }, (_, index) => {
            const angle = Math.PI / 8 + (index * Math.PI) / 4;
            return { x: mm(circum * Math.cos(angle)), z: mm(circum * Math.sin(angle)) };
        });
        return {
            shape,
            outline,
            convex: [outline],
            extent: { '+x': radius, '-x': radius, '+z': radius, '-z': radius },
            inscribedRadius: mm(radius - 0.002),
            wallHeight,
            intensity: input.intensity,
        };
    }
    const aspect = roleLayout.aspect.min + random.next() * (roleLayout.aspect.max - roleLayout.aspect.min);
    const short = mmUp(Math.max(need.radius, halfDoor, Math.sqrt(need.area / aspect) / 2));
    const long = mmUp(Math.max(short * aspect, need.area / (4 * short)));
    const alongX = random.next() < 0.5;
    const halfX = alongX ? long : short;
    const halfZ = alongX ? short : long;
    const main = rectPoints(-halfX, -halfZ, halfX, halfZ);
    const extent = { '+x': halfX, '-x': halfX, '+z': halfZ, '-z': halfZ };
    if (shape === 'rect') {
        return {
            shape,
            outline: main,
            convex: [main],
            extent,
            inscribedRadius: Math.min(halfX, halfZ),
            wallHeight,
            intensity: input.intensity,
        };
    }
    // L / T: a wing on a doorway-free side, built in a frame where that side is +z.
    const side = random.pick(freeSides);
    const rotation = SIDE_ROTATION[side];
    const across = side === '+z' || side === '-z' ? halfX : halfZ; // half the side's length
    const out = side === '+z' || side === '-z' ? halfZ : halfX; // centre → that side
    const depth = across;
    const [a, b] = shape === 't' ? [-across / 2, across / 2] : random.next() < 0.5 ? [-across, 0] : [0, across];
    const localMain = rectPoints(-across, -out, across, out);
    const localWing = rectPoints(a, out, b, out + depth);
    const localOutline = tidy([
        { x: -across, z: -out },
        { x: across, z: -out },
        { x: across, z: out },
        { x: b, z: out },
        { x: b, z: out + depth },
        { x: a, z: out + depth },
        { x: a, z: out },
        { x: -across, z: out },
    ]);
    const place = (points) => points.map((point) => {
        const turned = rotate(point, rotation);
        return { x: mm(turned.x) + 0, z: mm(turned.z) + 0 };
    });
    return {
        shape,
        outline: place(localOutline),
        convex: [place(localMain), place(localWing)],
        extent,
        inscribedRadius: Math.min(halfX, halfZ),
        wallHeight,
        intensity: input.intensity,
    };
}
/** Full extent of a chamber on each side, wings included (the band a cell must reserve). */
function reach(spec) {
    const xs = spec.outline.map((point) => point.x);
    const zs = spec.outline.map((point) => point.z);
    return {
        '+x': Math.max(...xs),
        '-x': -Math.min(...xs),
        '+z': Math.max(...zs),
        '-z': -Math.min(...zs),
    };
}
// ---------------------------------------------------------------- placement (§4.3)
function corridorBand(a, b) {
    if (a.role === 'rest' || b.role === 'rest')
        return 'short';
    if (a.role === 'traversal' || b.role === 'traversal')
        return 'long';
    return 'normal';
}
/**
 * Builds the plan's layout block. Throws when no doorway-free wall can host the
 * secret — the caller treats that as a rejected candidate (retry ladder).
 */
export function generateDungeonLayout(input) {
    const { rooms, connections, rules, random, theme, profile } = input;
    const themeLayout = theme.layout;
    const clearance = profile.clearance;
    const roomById = new Map(rooms.map((room) => [room.id, room]));
    const intensity = assignIntensity(rooms, input.criticalPathRoomIds, profile.pacing.beats);
    const doorSides = new Map(rooms.map((room) => [room.id, new Set()]));
    for (const connection of connections) {
        const a = roomById.get(connection.a);
        const b = roomById.get(connection.b);
        const dx = b.coordinate.x - a.coordinate.x;
        const dz = b.coordinate.z - a.coordinate.z;
        if (Math.abs(dx) + Math.abs(dz) !== 1) {
            throw new Error(`connection '${connection.id}' does not join grid neighbours`);
        }
        doorSides.get(a.id).add(sideOf(dx, dz));
        doorSides.get(b.id).add(sideOf(-dx, -dz));
    }
    // 1. size every chamber, in plan order
    const specs = new Map();
    for (const room of rooms) {
        specs.set(room.id, sizeChamber({
            room,
            doorSides: doorSides.get(room.id),
            intensity: intensity.get(room.id) ?? 0,
            partySize: input.partySize,
            profile,
            theme,
            rules,
            random,
        }));
    }
    // 2. corridor lengths drawn per connection from the role-pair band
    const drawn = new Map();
    for (const connection of connections) {
        const band = themeLayout.corridorLength[corridorBand(roomById.get(connection.a), roomById.get(connection.b))];
        drawn.set(connection.id, band.min + random.next() * (band.max - band.min));
    }
    // 3. variable-pitch grid: every column/row band is as wide as its widest
    //    chamber; the gap between two bands is the longest corridor drawn across
    //    it, never less than the secret alcove needs. Closed-form, so the
    //    "relaxation" converges in zero passes and holds no hidden state.
    const minGap = DUNGEON_LAYOUT_GLOBALS.secret.alcoveDepth + DUNGEON_LAYOUT_GLOBALS.bandMargin;
    const centres = (axis) => {
        const pos = axis === 'x' ? '+x' : '+z';
        const neg = axis === 'x' ? '-x' : '-z';
        const lines = [...new Set(rooms.map((room) => room.coordinate[axis]))].sort((p, q) => p - q);
        const halfPos = new Map();
        const halfNeg = new Map();
        for (const room of rooms) {
            const r = reach(specs.get(room.id));
            const line = room.coordinate[axis];
            halfPos.set(line, Math.max(halfPos.get(line) ?? 0, r[pos]));
            halfNeg.set(line, Math.max(halfNeg.get(line) ?? 0, r[neg]));
        }
        const gapAfter = new Map(lines.map((line) => [line, minGap]));
        for (const connection of connections) {
            const a = roomById.get(connection.a).coordinate[axis];
            const b = roomById.get(connection.b).coordinate[axis];
            if (a === b)
                continue;
            const line = Math.min(a, b);
            gapAfter.set(line, Math.max(gapAfter.get(line), drawn.get(connection.id)));
        }
        const out = new Map();
        let cursor = 0;
        lines.forEach((line, index) => {
            if (index > 0) {
                const previous = lines[index - 1];
                cursor += halfPos.get(previous) + gapAfter.get(previous) + halfNeg.get(line);
            }
            out.set(line, cursor);
        });
        return out;
    };
    const columnX = centres('x');
    const rowZ = centres('z');
    const entry = roomById.get(input.entryRoomId).coordinate;
    const originX = columnX.get(entry.x);
    const originZ = rowZ.get(entry.z);
    const centreOf = new Map(rooms.map((room) => [
        room.id,
        {
            x: mm(columnX.get(room.coordinate.x) - originX),
            z: mm(rowZ.get(room.coordinate.z) - originZ),
        },
    ]));
    const chambers = rooms.map((room) => {
        const spec = specs.get(room.id);
        const centre = centreOf.get(room.id);
        const shift = (points) => points.map((point) => ({ x: mm(point.x + centre.x), z: mm(point.z + centre.z) }));
        return {
            roomId: room.id,
            shape: spec.shape,
            outline: shift(spec.outline),
            convex: spec.convex.map(shift),
            centre,
            inscribedRadius: spec.inscribedRadius,
            wallHeight: spec.wallHeight,
            floorY: 0,
            intensity: spec.intensity,
        };
    });
    // 4. chokes (§4.4): only a critical-path corridor entering a build/peak
    //    chamber that is not an arena, drawn in path order so the consecutive
    //    count is honoured as it is validated.
    const chokes = new Set();
    const path = input.criticalPathRoomIds;
    let consecutive = 0;
    for (let i = 1; i < path.length; i += 1) {
        const from = path[i - 1];
        const to = path[i];
        const connection = connections.find((entry) => (entry.a === from && entry.b === to) || (entry.a === to && entry.b === from));
        const target = roomById.get(to);
        const source = roomById.get(from);
        const arena = (role) => role === 'boss' || role === 'miniboss';
        const eligible = connection !== undefined &&
            !arena(target.role) &&
            !arena(source.role) &&
            (intensity.get(to) ?? 0) >= DUNGEON_BEAT_INTENSITY.build &&
            consecutive < clearance.maxConsecutiveChokes;
        if (eligible && random.next() < themeLayout.chokeChance) {
            chokes.add(connection.id);
            consecutive += 1;
        }
        else {
            consecutive = 0;
        }
    }
    // 5. corridors, in connection order: wall of `a` → wall of `b`
    const corridors = connections.map((connection) => {
        const a = roomById.get(connection.a);
        const b = roomById.get(connection.b);
        const side = sideOf(b.coordinate.x - a.coordinate.x, b.coordinate.z - a.coordinate.z);
        const back = sideOf(a.coordinate.x - b.coordinate.x, a.coordinate.z - b.coordinate.z);
        const u = SIDE_VECTOR[side];
        const ca = centreOf.get(a.id);
        const cb = centreOf.get(b.id);
        const ea = specs.get(a.id).extent[side];
        const eb = specs.get(b.id).extent[back];
        const start = { x: mm(ca.x + u.x * ea), z: mm(ca.z + u.z * ea) };
        const end = { x: mm(cb.x - u.x * eb), z: mm(cb.z - u.z * eb) };
        const length = Math.abs(end.x - start.x) + Math.abs(end.z - start.z);
        const normal = (from, to) => ({
            from,
            to,
            clearWidth: rules.doorwayClearWidth,
            kind: 'normal',
        });
        const stub = DUNGEON_LAYOUT_GLOBALS.chokeDoorStub;
        const run = mm(Math.min(clearance.chokeMaxRunLength, length - 2 * stub));
        if (!chokes.has(connection.id) || run < 1) {
            return { connectionId: connection.id, segments: [normal(start, end)] };
        }
        const lead = mm((length - run) / 2);
        const p1 = { x: mm(start.x + u.x * lead), z: mm(start.z + u.z * lead) };
        const p2 = { x: mm(p1.x + u.x * run), z: mm(p1.z + u.z * run) };
        return {
            connectionId: connection.id,
            segments: [
                normal(start, p1),
                { from: p1, to: p2, clearWidth: clearance.chokeClearWidth, kind: 'choke' },
                normal(p2, end),
            ],
        };
    });
    // 6. the secret (§4.5): one doorway-free, axis-aligned wall of a non-boss chamber
    const secret = placeSecret(rooms, chambers, corridors, random);
    const points = [
        ...chambers.flatMap((chamber) => chamber.outline),
        ...secret.alcove,
        ...corridors.flatMap((corridor) => corridor.segments.flatMap(segmentFootprint)),
    ];
    const bounds = {
        minX: mm(Math.min(...points.map((p) => p.x))),
        minZ: mm(Math.min(...points.map((p) => p.z))),
        maxX: mm(Math.max(...points.map((p) => p.x))),
        maxZ: mm(Math.max(...points.map((p) => p.z))),
    };
    return {
        chambers,
        corridors,
        secret,
        bounds,
        metrics: analyzeDungeonLayout(chambers, corridors, bounds),
    };
}
function placeSecret(rooms, chambers, corridors, random) {
    const { entranceClearWidth: span, panelWidth, alcoveDepth } = DUNGEON_LAYOUT_GLOBALS.secret;
    const jamb = DUNGEON_LAYOUT_GLOBALS.doorwayJambWidth;
    const roleById = new Map(rooms.map((room) => [room.id, room.role]));
    const corridorParts = corridors.flatMap((corridor) => corridor.segments.map(segmentFootprint));
    const openings = corridors.flatMap((corridor) => [
        corridor.segments[0].from,
        corridor.segments[corridor.segments.length - 1].to,
    ]);
    const hosts = random.shuffle(chambers.filter((chamber) => roleById.get(chamber.roomId) !== 'boss'));
    for (const host of hosts) {
        const outline = host.outline;
        for (let i = 0; i < outline.length; i += 1) {
            const a = outline[i];
            const b = outline[(i + 1) % outline.length];
            const axisAligned = a.x === b.x || a.z === b.z;
            const length = Math.hypot(b.x - a.x, b.z - a.z);
            if (!axisAligned || length < span + 2 * jamb)
                continue;
            const ux = (b.x - a.x) / length;
            const uz = (b.z - a.z) / length;
            const onWall = (p) => Math.abs((p.x - a.x) * uz - (p.z - a.z) * ux) < 1e-6 &&
                (p.x - a.x) * ux + (p.z - a.z) * uz >= -1e-6 &&
                (p.x - a.x) * ux + (p.z - a.z) * uz <= length + 1e-6;
            if (openings.some(onWall))
                continue;
            // outward normal of a CCW outline edge is its right-hand side
            const nx = uz;
            const nz = -ux;
            const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
            const from = { x: mm(mid.x - (ux * span) / 2), z: mm(mid.z - (uz * span) / 2) };
            const to = { x: mm(from.x + ux * span), z: mm(from.z + uz * span) };
            const alcove = [
                from,
                { x: mm(from.x + nx * alcoveDepth), z: mm(from.z + nz * alcoveDepth) },
                { x: mm(to.x + nx * alcoveDepth), z: mm(to.z + nz * alcoveDepth) },
                to,
            ];
            const blocked = chambers.some((chamber) => chamber.convex.some((part) => convexOverlap(part, alcove))) ||
                corridorParts.some((part) => convexOverlap(part, alcove));
            if (blocked || polygonArea(alcove) === 0)
                continue;
            return {
                hostRoomId: host.roomId,
                gap: { from: { x: mm(from.x + ux * panelWidth), z: mm(from.z + uz * panelWidth) }, to },
                alcove,
            };
        }
    }
    throw new Error('no doorway-free wall can host the secret alcove');
}
//# sourceMappingURL=layout-stage.js.map